import { useMemo, useState } from 'react';

import { useActivityBuilderFlags } from 'approot/activity-builder/activity-builder-flags';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import SubActivityView from '../sub-activity/sub-activity-view';
import { ViewProps } from './draw.constants';
import * as Styled from './draw-view.style';
import { ZoomComponentTrigger } from 'approot/activity-builder/activity-builder-zoom-component-modal';
import { ZoomComponentModalProps } from 'approot/activity-builder/activity-builder-zoom-component-modal.constants';
import { useTeacherGetAnswers } from 'approot/activity-builder/present/activity-answers.hooks';
import type { DrawAnswerFragment } from '../../present/activity-answers.graphql.generated';
import DrawCanvasPreview from './draw-canvas-preview';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import ComponentOverlay from '../shared/component-overlay/component-overlay';
import { ComponentOverlayWrapper } from '../shared/component-overlay/component-overlay.style';
import DrawViewCheckAnswer from './draw-view-check-answer';
import { getSubActivityInstructionAsText } from '../sub-activity/sub-activity.utils';
import { useDrawLanguage } from './canvas/use-draw-language';

type Props = ViewProps & {
  onOpenDrawCanvas?: () => void;
} & ZoomComponentModalProps;

const DrawView = (props: Props) => {
  const {
    id,
    properties,
    subActivityProperties,
    openModal: openZoomModal,
    onOpenDrawCanvas,
  } = props;
  const [previewRef, setPreviewRef] = useState<HTMLDivElement | null>(null);
  const { activityBuilderRespondFlag } = useActivityBuilderFlags();
  const { isPublished } = useActivityBuilderPresentContext();
  const canRespond = isPublished && activityBuilderRespondFlag;
  const language = useDrawLanguage();

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const { componentAnswers, hasFetchedAnswers, error } = useTeacherGetAnswers({
    componentId: id,
    skip: !activityBuilderRespondFlag,
  });
  const answerData = componentAnswers?.at(0);

  const paddingTop = useMemo(
    () =>
      (properties.dimensionData.height / properties.dimensionData.width) * 100,
    [properties.dimensionData]
  );

  return (
    <SubActivityView subActivityDetails={subActivityProperties}>
      {!canRespond && openZoomModal && properties.image && (
        <ZoomComponentTrigger
          ariaLabel={`Expand to view: ${properties.altText}`}
          openModal={openZoomModal}
        />
      )}
      <Styled.DrawViewTeacher>
        <ComponentOverlayWrapper>
          <Styled.DrawCanvas
            data-selector="drawing-container"
            style={{
              paddingTop: `${paddingTop}%`,
            }}
          >
            <Styled.DrawViewPreview
              ref={setPreviewRef}
              role="image"
              aria-label={properties.altText || ''}
            >
              {previewRef && (
                <DrawCanvasPreview
                  id={id}
                  dimensionsData={properties.dimensionData}
                  imageUrl={properties.image?.original}
                  // this key is to re-render on data updates could instead use an effect
                  key={`${id}-${answerData?.uuid}-${answerData?.updatedAt}`}
                  containerEl={previewRef}
                  answer={(answerData?.answer as DrawAnswerFragment)?.drawing}
                  questionData={properties.draw}
                  language={language}
                />
              )}
            </Styled.DrawViewPreview>
          </Styled.DrawCanvas>
          {canRespond && (
            <ComponentOverlay
              ariaLabel={`Draw your answer to: ${getSubActivityInstructionAsText(
                subActivityProperties?.instructions
              )}`}
              componentId={id}
              hasFetchedAnswers={hasFetchedAnswers}
              error={error}
              onClick={
                canRespond
                  ? () => {
                      openModal();
                      onOpenDrawCanvas?.();
                    }
                  : undefined
              }
              getActiveStudentAnswerCount={answers => {
                return answers.filter(
                  response =>
                    (response.answer as DrawAnswerFragment).hasDrawingData
                ).length;
              }}
            />
          )}
        </ComponentOverlayWrapper>

        {isVisible && (
          <DrawViewCheckAnswer {...props} closeModal={closeModal} />
        )}
      </Styled.DrawViewTeacher>
    </SubActivityView>
  );
};

export default DrawView;
